import React, { useState } from 'react'
import Modal from '../../../../ui_reusable/Modal'
import { useDatasetsRegionDatasetsDestroy } from '../../../../../api/generated/endpoints/region-datasets/region-datasets'
import type { RegionDataset } from '../../../../../api/generated/models'
import { useQueryClient } from '@tanstack/react-query'
import { StandardButton } from '../../../../ui_reusable/styles/Button.styles'
import { ErrorMessage } from '../../../../ui_reusable/styles/CreateInputUtils.styles'

interface DeleteRegionDatasetProps {
  regionDataset: RegionDataset
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

const DeleteRegionDataset: React.FC<DeleteRegionDatasetProps> = ({
  regionDataset,
  open,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient()
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const deleteMutation = useDatasetsRegionDatasetsDestroy({
    mutation: {
      onSuccess: () => {
        // Invalidate driver list queries to refresh the databook
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/with-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/without-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/datasets/region-datasets/'],
        })

        if (onSuccess) {
          onSuccess()
        }
        onClose()
      },
      onError: (error: any) => {
        console.error('Failed to delete segment:', error)
        setError(error?.response?.data?.detail || 'Failed to delete segment')
        setIsDeleting(false)
      },
    },
  })

  const handleDelete = async () => {
    setIsDeleting(true)
    setError(null)

    try {
      await deleteMutation.mutateAsync({
        id: regionDataset.id!,
      })
    } catch (err) {
      // Error handled by mutation onError
    }
  }

  return (
    <Modal open={open} onClose={onClose} size="sm">
      <Modal.Header>
        <Modal.Title>Delete Segment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ marginBottom: '1.5rem' }}>
          <p style={{ marginBottom: '1rem' }}>
            Are you sure you want to delete this segment?
          </p>
          <div
            style={{
              backgroundColor: '#f5f5f5',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
            }}
          >
            <p>
              <strong>Region:</strong> {regionDataset.region?.name || 'Unknown'}
            </p>
            <p>
              <strong>Parent Dataset ID:</strong>{' '}
              {regionDataset.parent_id || 'No parent'}
            </p>
            <p>
              <strong>Total Observations:</strong>{' '}
              {regionDataset.total_observations || 0}
            </p>
          </div>
          <p style={{ color: '#d32f2f', fontWeight: 500 }}>
            This action cannot be undone.
          </p>
        </div>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <div
          style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            marginTop: '1.5rem',
          }}
        >
          <StandardButton type="button" onClick={onClose} disabled={isDeleting}>
            Cancel
          </StandardButton>
          <StandardButton
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            style={{
              backgroundColor: '#d32f2f',
              color: 'white',
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </StandardButton>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default DeleteRegionDataset
